import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

console.log('Rendering HeroSection');

const HeroSection = () => {
  const [currentProjectIndex, setCurrentProjectIndex] = useState(0);

  const featuredProjects = [
  {
    id: 1,
    title: "E-Commerce Platform Scaling",
    metric: "300% Performance Boost",
    description: "Transformed checkout flow reducing cart abandonment by 45%",
    image: "https://images.unsplash.com/photo-1515408201103-f7a8c194a15c",
    imageAlt: "Modern laptop displaying colorful e-commerce dashboard with graphs and analytics on dark wooden desk with coffee cup"
  },
  {
    id: 2,
    title: "SaaS Dashboard Rebuild",
    metric: "60% Faster Load Times",
    description: "Architected scalable React solution serving 50K+ daily users",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ff08559a-1765030143856.png",
    imageAlt: "Multiple computer monitors showing colorful data visualization charts and analytics dashboards in modern office setting"
  },
  {
    id: 3,
    title: "Real-Time Collaboration Tool",
    metric: "99.9% Uptime Achieved",
    description: "Built WebSocket infrastructure handling 10K concurrent connections",
    image: "https://images.unsplash.com/photo-1719253479632-61b2fdc77a65",
    imageAlt: "Close-up view of programming code on dark screen with blue and white syntax highlighting showing JavaScript functions"
  }];


  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentProjectIndex((prev) => (prev + 1) % featuredProjects?.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentProject = featuredProjects?.[currentProjectIndex];

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-background">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-accent/5" />
      <div className="relative z-10 max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-12 md:py-16 lg:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div className="space-y-6 md:space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-success/10 border border-success/20">
              <div className="w-2 h-2 rounded-full bg-success animate-pulse" />
              <span className="text-xs md:text-sm font-medium text-success">Available for Projects</span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-6xl font-bold font-heading text-foreground leading-tight">
              Production-Ready
              <span className="block text-primary mt-2">Problem Solver</span>
            </h1>

            <p className="text-base md:text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-xl">
              Senior Full-Stack Engineer bridging technical excellence with business acumen. Building scalable solutions for growth-stage startups and enterprise clients.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Button
                variant="default"
                size="lg"
                iconName="Rocket"
                iconPosition="right"
                className="w-full sm:w-auto"
                asChild>

                <Link to="/contact">Hire Me</Link>
              </Button>
              <Button
                variant="outline"
                size="lg"
                iconName="FolderOpen"
                iconPosition="left"
                className="w-full sm:w-auto"
                asChild>

                <Link to="/portfolio">View Projects</Link>
              </Button>
            </div>

            <div className="grid grid-cols-3 gap-4 md:gap-6 pt-6 md:pt-8 border-t border-border">
              <div>
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary">8+</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1">Years Experience</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary">150+</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1">Projects Delivered</div>
              </div>
              <div>
                <div className="text-2xl md:text-3xl lg:text-4xl font-bold text-primary">98%</div>
                <div className="text-xs md:text-sm text-muted-foreground mt-1">Client Satisfaction</div>
              </div>
            </div>
          </div>

          <div className="relative">
            <div className="relative aspect-[4/3] rounded-2xl overflow-hidden bg-card border border-border shadow-2xl">
              <Image
                src={currentProject?.image}
                alt={currentProject?.imageAlt}
                className="w-full h-full object-cover" />

              <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
              
              <div className="absolute bottom-0 left-0 right-0 p-4 md:p-6 lg:p-8">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/20 backdrop-blur-sm border border-primary/30 mb-3">
                  <Icon name="TrendingUp" size={14} color="var(--color-primary)" />
                  <span className="text-xs font-medium text-primary">{currentProject?.metric}</span>
                </div>
                <h3 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground mb-2">
                  {currentProject?.title}
                </h3>
                <p className="text-sm md:text-base text-muted-foreground">
                  {currentProject?.description}
                </p>
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-4">
              {featuredProjects?.map((_, index) =>
              <button
                key={index}
                onClick={() => setCurrentProjectIndex(index)}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentProjectIndex ?
                'bg-primary w-8' : 'bg-muted-foreground/30 hover:bg-muted-foreground/50'}`
                }
                aria-label={`View project ${index + 1}`} />

              )}
            </div>
          </div>
        </div>
      </div>
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <Icon name="ChevronDown" size={32} className="text-muted-foreground" />
      </div>
    </section>);

};

export default HeroSection;